import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import Quiz from "../../components/Quiz";
import "../../assets/css/SidebarComponents/Quiz.css";
import { useNavigate } from "react-router-dom";
import { fetchLevel, fetchXp, increaseXp, increaseLevel, decreaseXp, getQuizXp } from "apiHandles/user_progress";
import { useContext } from "react";
import { UserProgressContext } from "../../context/UserProgressContext";
import { isQuizDone, setQuizDone, increaseSubStructure, increaseStructure } from "../../apiHandles/user_progress.js"
import { useQuizNavigationBlocker } from "../../hooks/useQuizNavigationBlocker";
import { useDarkMode } from "context/DarkModeProvider";
import "../../assets/css/colors.css"
import { useLanguage } from "context/LanguageProvider";
import axiosInstance from "apiHandles/axiosInstance";
import CryptoJS from 'crypto-js';

const SECRET_KEY = process.env.REACT_APP_STRIPE_ENCRYPTION_SECRET_KEY

function decryptData(encryptedData) {
  try {
    // Δημιουργία 32-byte key από το SECRET_KEY (ίδιο με backend)
    const key = CryptoJS.SHA256(SECRET_KEY);

    const decrypted = CryptoJS.AES.decrypt(
      encryptedData,
      key,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    );

    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error("Decryption failed:", error);
    return null;
  }
}

const subIds = {
  6: "Directed",
  7: "Undirected",
  3: "Binary Tree",
  4: "AVL Tree",
  5: "Heap",
  9: "Singly Linked List",
  10: "Doubly Linked List",
  11: "1D Array",
  12: "2D Array",
  15: "Array-based Stack",
  16: "Linked List Stack",
  17: "Circular Queue",
  18: "Priority Queue",
  19: "Chaining",
  20: "Open Addressing",
  21: "Hash Set",
  22: "Tree Set",
  24: "Dynamic Array",
  35: "Basics",
  36: "Advanced"
};

const dataPercentage = {
  "Directed": [12.5, 7.1],
  "Undirected": [16.6, 7.1],
  "Binary Tree": [12.5, 4.1],
  "AVL Tree": [12.5, 4.1],
  "Heap": [12.5, 4.1],
  "Singly Linked List": [12.5, 6.25],
  "Doubly Linked List": [12.5, 6.25],
  "1D Array": [12.5, 4.1],
  "2D Array": [10, 4.1],
  "Dynamic Array": [16.6, 4.1],
  "Chaining": [12.5, 7.1],
  "Open Addressing": [16.6, 7.1],
  "Circular Queue": [12.5, 7.1],
  "Priority Queue": [16.6, 7.1],
  "Array-based Stack": [16.6, 10.0],
  "Linked List Stack": [25.0, 10.0],
  "Hash Set": [16.6, 8.3],
  "Tree Set": [16.6, 8.3],
  "Basics": [16.6, 7.1],
  "Advanced": [12.5, 7.1],
};

const QuizPage = () => {
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const { selLanguage } = useLanguage();

  const [quizId, setQuizId] = useState(null);
  const [quizMetadata, setQuizMetadata] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [nextQuestion, setNextQuestion] = useState(null); // ✅ Prefetch
  const [loadingQuestion, setLoadingQuestion] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false); // ✅ Transition

  const [score, setScore] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [xp, setXp] = useState(0);
  const [level, setLevel] = useState(null);
  const updatedRef = useRef(false);
  const [showScore, setShowScore] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const { updateXp, updateLevel } = useContext(UserProgressContext);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [quizAlreadyDone, setQuizAlreadyDone] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(true);
  const [checkingQuizStatus, setCheckingQuizStatus] = useState(true);
  const [quizXp, setQuizXp] = useState(0);
  const [firstTry, setFirstTry] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [continueNavigation, setContinueNavigation] = useState(null);
  const [selectedStageId, setSelectedStageId] = useState(null);

  const questionsCache = useRef({});

  var stars = 0;

  useEffect(() => {
    const loadQuizMetadata = async () => {
      try {
        const stageId = localStorage.getItem("selectedStageId");

        if (!stageId) {
          console.error("No stage ID found in localStorage");
          navigate("/HomePage/homepage");
          return;
        }

        setSelectedStageId(stageId);

        const response = await axiosInstance.get(`/api/stage/${stageId}/quiz/`);
        const encryptedData = response.data.data;
        const decrtypted = decryptData(encryptedData);
        const quizData = decrtypted;

        setQuizId(quizData.id);
        setQuizMetadata(quizData);
        setTotalQuestions(quizData.total_questions);

      } catch (error) {
        console.error("Failed to load quiz metadata:", error);
        alert("Σφάλμα κατά τη φόρτωση του quiz");
        navigate("/HomePage/homepage");
      }
    };

    loadQuizMetadata();
  }, [navigate]);

  const fetchQuestion = async (index) => {
    if (questionsCache.current[index]) {
      return questionsCache.current[index];
    }

    try {
      console.log(`🔍 Fetching question ${index} for quiz:`, quizId);
      const response = await axiosInstance.get(
        `/api/quiz/${quizId}/question/${index}/`
      );

      const encryptedData = response.data.data;

      if (!encryptedData) {
        console.error("No encrypted data received");
        throw new Error("No encrypted data received");
      }

      const decryptedData = decryptData(encryptedData);

      if (!decryptedData) {
        console.error("Decryption failed");
        throw new Error("Decryption failed");
      }

      questionsCache.current[index] = decryptedData;
      console.log(`✅ Question ${index} loaded, decrypted, and cached`);
      return decryptedData;
    } catch (error) {
      console.error(`❌ Error fetching question ${index}:`, error);
      throw error;
    }
  };

  useEffect(() => {
    if (!quizId || totalQuestions === 0) return;

    const loadCurrentQuestion = async () => {
      setLoadingQuestion(true);

      try {
        // Αν έχουμε prefetch, χρησιμοποίησέ το
        if (nextQuestion && nextQuestion.current_index === currentQuestionIndex) {
          console.log(`✅ Using prefetched question ${currentQuestionIndex}`);
          setCurrentQuestion(nextQuestion);
          setNextQuestion(null);
          setLoadingQuestion(false);
        } else {
          // ✅ Το fetchQuestion επιστρέφει ήδη decrypted data
          const question = await fetchQuestion(currentQuestionIndex);
          setCurrentQuestion(question);
          setLoadingQuestion(false);
        }
      } catch (error) {
        alert("Σφάλμα κατά τη φόρτωση της ερώτησης");
        setLoadingQuestion(false);
      }
    };

    loadCurrentQuestion();
  }, [currentQuestionIndex, quizId, totalQuestions, nextQuestion]);

  useEffect(() => {
    if (!quizId || totalQuestions === 0 || loadingQuestion) return;

    const nextIndex = currentQuestionIndex + 1;

    // Αν υπάρχει επόμενη ερώτηση
    if (nextIndex < totalQuestions) {
      const prefetchNext = async () => {
        try {
          const question = await fetchQuestion(nextIndex);
          setNextQuestion(question);
        } catch (error) {
          console.error(`Failed to prefetch question ${nextIndex}`);
        }
      };

      // Περίμενε λίγο πριν κάνεις prefetch (για να μην μπλοκάρει το UI)
      const timer = setTimeout(prefetchNext, 300);
      return () => clearTimeout(timer);
    }
  }, [currentQuestionIndex, quizId, totalQuestions, loadingQuestion]);

  useQuizNavigationBlocker(
    firstTry && !isFinished && !quizAlreadyDone,
    (proceed) => {
      setShowLeaveModal(true);
      setContinueNavigation(() => proceed);
    }
  );

  useEffect(() => {
    if (firstTry && !isFinished && !quizAlreadyDone) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = "Αν φύγεις τώρα, θα χαθεί η πρόοδός σου και δεν θα μπορείς να ξαναπάρεις XP από αυτό το quiz!";
        return e.returnValue;
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [firstTry, isFinished, quizAlreadyDone]);

  const handleCheckQuizDone = async () => {
    if (!selectedStageId) return;

    try {
      const quizDone = await isQuizDone(selectedStageId);
      setQuizAlreadyDone(quizDone);
    } catch (error) {
      console.error("Error checking quiz status:", error);
      setQuizAlreadyDone(false);
    } finally {
      setCheckingQuizStatus(false);
    }
  };

  useEffect(() => {
    if (selectedStageId) {
      handleCheckQuizDone();
    }
  }, [selectedStageId]);

  React.useEffect(() => {
    const getXp = async () => {
      const xp = await fetchXp();
      setXp(xp || 0);
    };
    getXp();
  }, []);

  React.useEffect(() => {
    const getLevel = async () => {
      const level = await fetchLevel();
      setLevel(level);
    };
    getLevel();
  }, []);

  useEffect(() => {
    const getQuizXP = async () => {
      if (!selectedStageId) return;

      const fetchedXp = await getQuizXp(selectedStageId);
      setQuizXp(fetchedXp);
    };
    getQuizXP();
  }, [selectedStageId]);

  const handleAnswerSelected = (isCorrect) => {
    if (isCorrect) setScore((prev) => prev + 1);
    setHasAnswered(true);
  };

  const handleNextQuestion = () => {
    if (!hasAnswered) {
      alert(selLanguage
        ? "Πρέπει να απαντήσεις πριν προχωρήσεις στην επόμενη ερώτηση."
        : "You must answer before proceeding to the next question.");
      return;
    }

    // ✅ Smooth transition
    setIsTransitioning(true);

    setTimeout(() => {
      if (currentQuestionIndex + 1 === totalQuestions) {
        setIsFinished(true);
        setShowScore(true);
        setPendingUpdate(true);
      } else {
        setCurrentQuestionIndex((prev) => prev + 1);
        setHasAnswered(false);
      }
      setIsTransitioning(false);
    }, 200); // 200ms fade out
  };

  const handleRestart = () => {
    setScore(0);
    setCurrentQuestionIndex(0);
    setIsFinished(false);
    navigate("/admin/index");
  };

  const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;

  let resultColor = "";
  let resultMessage = "";

  if (percentage < 30) {
    resultColor = "#e53935";
    resultMessage = selLanguage
      ? "Καλή προσπάθεια. Διάβασε πιο προσεκτικά και ξανά δοκίμασε το quiz."
      : "Good effort. Read more carefully and try the quiz again.";
    stars = 1;
    if (!quizAlreadyDone || firstTry) {
      resultMessage += selLanguage
        ? ` Κέρδισες ${score * quizXp} XP!`
        : ` You earned ${score * quizXp} XP!`;
    }
  } else if (percentage < 70) {
    resultColor = "#fbc02d";
    resultMessage = selLanguage
      ? "Πολύ καλή προσπάθεια! Συνέχισε την καλή δουλειά."
      : "Very good effort! Keep up the good work.";
    stars = 2;
    if (!quizAlreadyDone || firstTry) {
      resultMessage += selLanguage
        ? ` Κέρδισες ${score * quizXp} XP!`
        : ` You earned ${score * quizXp} XP!`;
    }
  } else {
    resultColor = "#43a047";
    resultMessage = selLanguage
      ? "Καταπληκτική δουλειά! Τα πήγες περίφημα."
      : "Amazing work! You did great.";
    stars = 3;
    if (!quizAlreadyDone || firstTry) {
      resultMessage += selLanguage
        ? ` Κέρδισες ${score * quizXp} XP!`
        : ` You earned ${score * quizXp} XP!`;
    }
  }

  useEffect(() => {
    if (isFinished && showScore && pendingUpdate) {
      const timer = setTimeout(() => {
        setShowScore(false);
        setPendingUpdate(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isFinished, showScore, pendingUpdate]);

  useEffect(() => {
    if (isFinished && !showScore && score > 0 && !updatedRef.current && !quizAlreadyDone) {
      const updateUserProgress = async () => {
        try {
          const xpToAdd = score * quizXp;
          const updatedXp = await increaseXp(xpToAdd);

          if (updatedXp !== null) {
            updatedRef.current = true;
            const xpRequired = level * 100;

            if (updatedXp >= xpRequired) {
              const leftXp = updatedXp - xpRequired;
              await increaseLevel(1);
              await decreaseXp();
              await increaseXp(leftXp);
              const newLevel = await fetchLevel();

              setLevel(newLevel);
              setXp(leftXp);
              updateLevel(newLevel);
              updateXp(leftXp);
            } else {
              setXp(updatedXp);
              updateXp(updatedXp);
            }
          }
        } catch (error) {
          console.error("Error updating user progress:", error);
        }
      };

      updateUserProgress();
    }
  }, [isFinished, showScore, score, level, quizAlreadyDone, updateLevel, updateXp, quizXp]);

  if (checkingQuizStatus || !quizMetadata || (totalQuestions > 0 && loadingQuestion && !currentQuestion)) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0f172a",
        padding: "1rem",
      }}>
        <div style={{
          background: "rgba(30, 41, 59, 0.95)",
          borderRadius: "1rem",
          padding: "2rem",
          color: "white",
          textAlign: "center"
        }}>
          <p>{selLanguage ? "Φόρτωση quiz..." : "Loading quiz..."}</p>
        </div>
      </div>
    );
  }

  // ✅ Αν δεν υπάρχει quiz
  if (!quizMetadata || totalQuestions === 0) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0f172a",
        padding: "1rem",
      }}>
        <div style={{
          background: "rgba(30, 41, 59, 0.95)",
          borderRadius: "1rem",
          padding: "2rem",
          color: "white",
          textAlign: "center"
        }}>
          <p>{selLanguage ? "Δεν βρέθηκε το quiz" : "Quiz not found"}</p>
          <button onClick={() => navigate("/HomePage/homepage")}>
            {selLanguage ? "Επιστροφή" : "Go Back"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quizMainContainer">
      {/* Leave Modal */}
      <Modal
        isOpen={showLeaveModal}
        centered
        contentClassName="no-shadow-modal"
      >
        <div style={{
          background: "#EFEFEF",
          borderRadius: '0.5rem',
          maxWidth: "100%"
        }}>
          <ModalHeader style={{
            fontSize: "clamp(1.2rem, 4vw, 1.5rem)",
            textAlign: "center",
            color: "#c04646",
            justifyContent: "center",
            background: darkMode ? "#1f2937 100%" : "var(--lightModeModal)"
          }}>
            {selLanguage ? " Προειδοποίηση!" : " Warning!"}
          </ModalHeader>
          <ModalBody style={{
            padding: "clamp(1rem, 3vw, 2rem)",
            background: darkMode ? "#1f2937 100%" : "var(--lightModeModal)"
          }}>
            <p style={{
              textAlign: "center",
              color: darkMode ? "#ffffffff" : "#040404ff",
              fontFamily: "Poppins, sans-serif",
              fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
              lineHeight: "1.5",
              marginBottom: "1.5rem",
              background: darkMode ? "#1f2937 100%" : "var(--lightModeModal)"
            }}>
              {selLanguage ? (
                <>
                  Αν φύγεις τώρα, θα χαθεί η πρόοδός σου και δεν θα μπορείς να ξαναπάρεις XP από αυτό το quiz!<br />
                  Θέλεις να συνεχίσεις ή να φύγεις;
                </>
              ) : (
                <>
                  If you leave now, your progress will be lost and you won't be able to earn XP from this quiz again!<br />
                  Do you want to continue or leave?
                </>
              )}
            </p>
            <div style={{
              display: "flex",
              justifyContent: "center",
              gap: "1rem",
              flexWrap: "wrap"
            }}>
              <Button
                onClick={() => {
                  setShowLeaveModal(false);
                  if (continueNavigation) continueNavigation();
                }}
                style={{
                  background: "linear-gradient(135deg, #b81e1eff 0%, #7c0505ff 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "0.5rem",
                  padding: "0.75rem 1.5rem",
                  fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                  minWidth: "100px"
                }}
              >
                {selLanguage ? "Φύγε" : "Leave"}
              </Button>
              <Button
                color="primary"
                onClick={() => setShowLeaveModal(false)}
                style={{
                  borderRadius: "0.5rem",
                  padding: "0.75rem 1.5rem",
                  fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                  minWidth: "100px",
                  background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                }}
              >
                {selLanguage ? "Συνέχισε το Quiz" : "Continue Quiz"}
              </Button>
            </div>
          </ModalBody>
        </div>
      </Modal>

      {/* Welcome Modal */}
      <Modal isOpen={showWelcomeModal} centered contentClassName="no-shadow-modal">
        <div style={{
          background: darkMode ? "#1e293b" : "var(--lightModeModal)",
          borderRadius: '0.5rem',
          maxWidth: "90vw"
        }}>
          <ModalHeader
            style={{
              fontSize: "clamp(1.2rem, 4vw, 1.5rem)",
              textAlign: "center",
              color: "#cf3838ff",
              justifyContent: "center"
            }}>
            {selLanguage ? "Προειδοποίηση!" : "Warning!"}
          </ModalHeader>

          <ModalBody style={{ padding: "clamp(1rem, 3vw, 2rem)" }}>
            <p style={{
              textAlign: "center",
              color: darkMode ? "#e4e4e4ff" : "#1b1b1bd5",
              fontFamily: "Poppins, sans-serif",
              fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            }}>
              {quizAlreadyDone
                ? (
                  <>
                    {selLanguage ? (
                      <>
                        Έχεις ήδη ολοκληρώσει αυτό το quiz.<br />
                        Δεν θα πάρεις ξανά XP, αλλά μπορείς να το κάνεις για εξάσκηση!
                      </>
                    ) : (
                      <>
                        You have already completed this quiz.<br />
                        You will not earn XP again, but you can take it for practice!
                      </>
                    )}
                  </>
                )
                : (
                  <>
                    {selLanguage ? (
                      <>
                        Το quiz παρέχει XP μόνο στην πρώτη ολοκλήρωση.<br />
                        Μπορείτε να το επαναλάβετε οποιαδήποτε στιγμή για να τεστάρετε τις γνώσεις σας, αλλά δεν θα λάβετε επιπλέον XP :)
                      </>
                    ) : (
                      <>
                        You will earn XP only once for this quiz.<br />
                        You can retake it for practice, but no further XP will be awarded :)
                      </>
                    )}
                  </>
                )
              }
            </p>
            <div style={{
              display: "flex",
              justifyContent: "center",
              gap: "1rem",
              flexWrap: "wrap"
            }}>
              <Button
                onClick={() => navigate(`/admin/index`)}
                style={{
                  background: "linear-gradient(135deg, #c73434ff 0%, #960909ff 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "0.5rem",
                  padding: "0.75rem 1.5rem",
                  fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                  minWidth: "100px"
                }}
              >
                {selLanguage ? "Πίσω" : "Back"}
              </Button>
              <Button
                onClick={() => {
                  setShowWelcomeModal(false);
                  if (!quizAlreadyDone && selectedStageId) {
                    setFirstTry(true);
                    setQuizDone(selectedStageId);

                    // ✅ Διάβασε το substructureId από localStorage
                    const subStructureId = localStorage.getItem("currentSubstructureId");

                    if (subStructureId) {
                      const subStructure = subIds[subStructureId];
                      const values = dataPercentage[subStructure];

                      if (values && Array.isArray(values) && values.length >= 2) {
                        increaseSubStructure(values[0], selectedStageId);
                        increaseStructure(values[1], selectedStageId, subStructureId);
                      }
                    }
                  }
                }}
                style={{
                  borderRadius: "0.5rem",
                  padding: "0.75rem 1.5rem",
                  fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                  minWidth: "100px",
                  background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                  color: "#fff",
                  border: "none"
                }}
              >
                {selLanguage ? "Έναρξη" : "Start"}
              </Button>
            </div>
          </ModalBody>
        </div>
      </Modal>

      {/* Main Quiz Container */}
      <div className="MainQuizComponent" style={{
        opacity: isTransitioning ? 0 : 1,
        transition: 'opacity 0.2s ease-in-out'
      }}>
        {!currentQuestion ? (
          <div style={{
            background: "rgba(30, 41, 59, 0.95)",
            borderRadius: "1rem",
            padding: "2rem",
            color: "white",
            textAlign: "center"
          }}>
            <p>{selLanguage ? "Φόρτωση ερώτησης..." : "Loading question..."}</p>
          </div>
        ) : !isFinished ? (
          <Quiz
            key={currentQuestionIndex}
            question={currentQuestion.question_text}
            options={currentQuestion.options}
            currentQuestionId={currentQuestion.id}
            onAnswerSelected={handleAnswerSelected}
            questionIndex={currentQuestionIndex + 1}
            totalQuestions={totalQuestions}
            onNextQuestion={handleNextQuestion}
          />
        ) : showScore ? (
          <div className="quizFinalScore">
            <div className="stars">
              {[...Array(3)].map((_, i) => (
                <span key={i} className={`star ${i < stars ? 'yellow' : 'white'}`}>★</span>
              ))}
            </div>

            <div className="percentage">
              {score} / {totalQuestions}
            </div>

            <p className="quizScoreMsg">
              {resultMessage}
            </p>
          </div>
        ) : (
          <div className="quizFinalScore" style={{
            background: darkMode ? "rgba(30, 41, 59, 0.95)" : "#ffffff",
            color: darkMode ? "white" : "#000000"
          }}>
            <div className="stars">
              {[...Array(3)].map((_, i) => (
                <span key={i} className={`star ${i < stars ? 'yellow' : 'white'}`}>★</span>
              ))}
            </div>

            <div className="percentage">
              {score} / {totalQuestions}
            </div>

            <p className="quizScoreMsg">
              {resultMessage}
            </p>

            <button
              onClick={handleRestart}
              className="continueButtonQuiz"
              style={{
                background: darkMode ? "#334155" : "#f8fafc",
                border: darkMode ? "2px solid #475569" : "none",
                color: darkMode ? "#f8fafc" : "#494949"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = darkMode ? "#475569" : "#f0f0f0";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = darkMode ? "#334155" : "#f8fafc";
              }}
            >
              {selLanguage ? " Συνέχεια" : "Continue"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizPage;